import {
  INotification,
  Vehicle,
} from '@otw/models'
import { AdminNotificationType } from '@otw/models/notifications/enums/enums'
import { PrivateUserInfoType } from '@otw/models/user/user'
import {
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Business as BusinessIcon,
  AssignmentTurnedIn as ApplicationIcon,
  Handyman as PartIcon,
  EventAvailable as PTOIcon,
  Notifications as NotificationsIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
} from '@mui/icons-material'

/**
 * Type guard to check if a string is an AdminNotificationType
 */
function isAdminNotificationType(
  type: string
): type is AdminNotificationType {
  const adminTypes = Object.values(AdminNotificationType);
  const isAdmin = adminTypes.includes(type as AdminNotificationType);
  
  return isAdmin;
}

function getTransportSystemStatusMessage(status: unknown): string | null {
  if (status === "already_in_system") {
    return "Already in transport system";
  }
  if (status === "not_in_system") {
    return "Not in transport system yet";
  }
  return null;
}

function formatLast4Vin(vin: unknown): string | null {
  if (typeof vin !== "string") {
    return null;
  }
  const cleanedVin = vin.trim();
  if (!cleanedVin) {
    return null;
  }
  const last4 = cleanedVin.slice(-4);
  return `${last4}`;
}

/**
 * Gets the appropriate icon component for a notification type
 * @param type The notification type
 * @param severity Optional severity to override icon based on importance
 * @returns The icon component to use
 */
export const getIcon = (type: string, severity?: string) => {
  // If severity is provided, use severity-based icons
  if (severity) {
    switch (severity) {
      case "error":
        return ErrorIcon;
      case "warning":
        return WarningIcon;
      case "success":
        return SuccessIcon;
      case "info":
        return InfoIcon;
    }
  }

  // Handle admin notification types
  if (isAdminNotificationType(type)) {
    switch (type) {
      // User-related admin notifications
      case AdminNotificationType.ADMIN_USER_SET_INITIAL_PASSWORD:
      case AdminNotificationType.ADMIN_USER_CREATED_EVENT:
        return PersonIcon;

      // Lead-related admin notifications
      case AdminNotificationType.ADMIN_LEAD_CREATED_EVENT:
      case AdminNotificationType.ADMIN_LEAD_SHIPPING_INFORMATION_NEEDED:
      case AdminNotificationType.ADMIN_LEAD_SHIPPING_QUOTE_COMPLETE:
        return BusinessIcon;

      // Deal-related admin notifications
      case AdminNotificationType.ADMIN_DEAL_CREATED_EVENT:
      case AdminNotificationType.ADMIN_DEAL_STATUS_CHANGE_EVENT:
        return BusinessIcon;

      // Application-related admin notifications
      case AdminNotificationType.ADMIN_APPLICATION_CREATED_EVENT:
      case AdminNotificationType.ADMIN_APPLICATION_STATUS_CHANGE_EVENT:
        return ApplicationIcon;

      // Inventory-related admin notifications
      case AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED:
      case AdminNotificationType.ADMIN_INVENTORY_VEHICLE_DELETED:
      case AdminNotificationType.ADMIN_INVENTORY_PUBLISHED_ON_SITE_EVENT:
      case AdminNotificationType.ADMIN_INVENTORY_SOLD_EVENT:
      case AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_NEEDED:
      case AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_COMPLETED:
      case AdminNotificationType.ADMIN_VEHICLE_GATE_PASS_NEEDED:
      case AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_TITLE_COPY_NEEDED:
      case AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_NOT_PURCHASED:
      case AdminNotificationType.ADMIN_VEHICLE_FUNDED_NEEDS_SOLD:
      case AdminNotificationType.ADMIN_VEHICLE_PURCHASED_NEEDS_PICKUP_LOCATION:
      case AdminNotificationType.ADMIN_VEHICLE_PAID_FOR_TITLE_REQUEST:
      case AdminNotificationType.ADMIN_VEHICLE_FUNDED:
      case AdminNotificationType.ADMIN_TITLE_RECEIVED:
      case AdminNotificationType.ADMIN_TITLE_OVERVIEW:
      case AdminNotificationType.ADMIN_TITLE_REMINDER:
      case AdminNotificationType.ADMIN_SOLD_TITLE_IN_TRANSIT_REMINDER:
      case AdminNotificationType.ADMIN_ELECTRONIC_TITLE_LIEN_RELEASE_REMINDER:
      case AdminNotificationType.ADMIN_INVENTORY_PAYOFF_EXPIRING:
      case AdminNotificationType.SALES_REP_REPAIR_COMPLETE:
      case AdminNotificationType.SALES_REP_DELIVERY_COMPLETE:
      case AdminNotificationType.SALES_REP_VEHICLE_FUNDED:
      case AdminNotificationType.ADMIN_MANAGEMENT_REPAIR_COMPLETE:
      case AdminNotificationType.ADMIN_MANAGEMENT_DELIVERY_COMPLETE:
      case AdminNotificationType.STOCK_NUMBER_PICKUP_LOCATION_NEEDED:
      case AdminNotificationType.STOCK_NUMBER_GATE_PASS_NEEDED:
      case AdminNotificationType.STOCK_NUMBER_PENDING_SALE_TITLE_COPY_NEEDED:
      case AdminNotificationType.STOCK_NUMBER_PURCHASED_NEEDS_PICKUP_LOCATION:
        return CarIcon;

      // Transporter-related admin notifications
      case AdminNotificationType.ADMIN_TRANSPORTER_PHOTOS_COMPLETED:
      case AdminNotificationType.ADMIN_TRANSPORTER_VEHICLE_READY_FOR_TRANSPORT:
      case AdminNotificationType.ADMIN_TRANSPORTER_PACKAGE_COMPLETION_REQUESTED:
      case AdminNotificationType.ADMIN_TRANSPORTER_DRIVER_CREATION_REQUEST:
      case AdminNotificationType.ADMIN_TRANSPORTER_INSPECTION_CHECKLIST_COMPLETED:
      case AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_ESTIMATE_UPLOADED:
      case AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_TIMELINE_UPDATED:
      case AdminNotificationType.ADMIN_TRANSPORTER_SHIPMENT_REJECTED:
      case AdminNotificationType.ADMIN_TRANSPORTER_SLA_OVERDUE_DAILY:
      case AdminNotificationType.ADMIN_OVERDUE_VEHICLE_REMINDER:
      case AdminNotificationType.ADMIN_REPAIR_5_DAYS_REMINDER:
      case AdminNotificationType.STOCK_NUMBER_PAYOFF_EXPIRING:
      case AdminNotificationType.STOCK_NUMBER_TITLE_OVERVIEW:
      case AdminNotificationType.STOCK_NUMBER_TITLE_REMINDER:
        return CarIcon;

      // Part-related admin notifications
      case AdminNotificationType.ADMIN_PART_REQUESTED:
        return PartIcon;

      // Time tracker admin notifications
      case AdminNotificationType.ADMIN_PTO_REQUEST_CREATED:
        return PTOIcon;
    }
  }

  // Default icon
  return NotificationsIcon;
};

/**
 * Formats a notification message based on its type and data
 * @param notification The notification object
 * @param users Array of users for context
 * @param vehicles Array of vehicles for context
 * @returns Formatted message string
 */
export const getNotificationMessage = (
  notification: INotification,
  users: PrivateUserInfoType[],
  vehicles: Vehicle[],
): string => {
  const { type } = notification;

  // Handle admin notification types
  if (isAdminNotificationType(type)) {
    try {
      switch (type) {
        // User-related admin notifications
        case AdminNotificationType.ADMIN_USER_SET_INITIAL_PASSWORD:
          const userPasswordData = (notification as any).afterData || (notification as any).beforeData;
          if (userPasswordData && userPasswordData.first_name && userPasswordData.last_name) {
            return `${userPasswordData.first_name} ${userPasswordData.last_name} has set their initial password`;
          }
          return "User has set their initial password";

        case AdminNotificationType.ADMIN_USER_CREATED_EVENT:
          const userCreatedData = (notification as any).afterData;
          if (userCreatedData && userCreatedData.first_name && userCreatedData.last_name) {
            return `New user created: ${userCreatedData.first_name} ${userCreatedData.last_name} (${userCreatedData.email || 'No email'})`;
          }
          return "New user has been created";

        // Lead-related admin notifications
        case AdminNotificationType.ADMIN_LEAD_CREATED_EVENT:
          const leadCreatedData = (notification as any).afterData;
          if (leadCreatedData && leadCreatedData.customerName) {
            let message = "New lead created";
            
            // Add the name of the user who created the lead at the beginning
            if (leadCreatedData.createdBy && users.length > 0) {
              const createdByUser = users.find(user => user.id === leadCreatedData.createdBy);
              if (createdByUser && createdByUser.first_name && createdByUser.last_name) {
                message += ` by ${createdByUser.first_name} ${createdByUser.last_name}`;
              }
            }
            
            message += `: ${leadCreatedData.customerName}`;
            if (leadCreatedData.company) {
              message += ` from ${leadCreatedData.company}`;
            }
            if (leadCreatedData.priority) {
              message += ` (${leadCreatedData.priority} priority)`;
            }
            if (leadCreatedData.source) {
              message += ` (${leadCreatedData.source})`;
            }
            return message;
          }
          return "New lead has been created";

        case AdminNotificationType.ADMIN_LEAD_SHIPPING_INFORMATION_NEEDED: {
          const shippingLeadData = (notification as any).afterData;
          if (shippingLeadData) {
            const leadLabel =
              shippingLeadData.leadNumber?.trim() || shippingLeadData.id || 'Unknown lead';
            let message = `Shipping information needed for lead ${leadLabel}`;
            if (shippingLeadData.company) {
              message += ` (${shippingLeadData.company})`;
            }

            const trucks = shippingLeadData.notification_trucks_needing_locations;
            if (Array.isArray(trucks) && trucks.length > 0) {
              const truckSummaries = trucks.map((truck: {
                stockNumber?: string | null;
                missingOrigin?: boolean;
                missingDropoff?: boolean;
              }) => {
                const stockLabel = truck.stockNumber ? `#${truck.stockNumber}` : 'Truck';
                const missingParts: string[] = [];
                if (truck.missingOrigin) missingParts.push('origin');
                if (truck.missingDropoff) missingParts.push('dropoff');
                return `${stockLabel} (missing ${missingParts.join(' and ')})`;
              });
              message += `: ${truckSummaries.join(', ')}`;
            }

            return message;
          }
          return "Shipping information is needed for linked lead trucks";
        }

        case AdminNotificationType.ADMIN_LEAD_SHIPPING_QUOTE_COMPLETE: {
          const quoteLeadData = (notification as any).afterData;
          if (quoteLeadData) {
            const leadLabel =
              quoteLeadData.leadNumber?.trim() || quoteLeadData.id || 'Unknown lead';
            let message = `Shipping quote complete for lead ${leadLabel}`;
            if (quoteLeadData.company) {
              message += ` (${quoteLeadData.company})`;
            }

            const snapshot = quoteLeadData.shipping_quote_snapshot;
            if (snapshot) {
              const total = snapshot.totalShippingCost;
              const truckCount = snapshot.quotedCount ?? snapshot.trucks?.length ?? 0;
              if (typeof total === 'number' && Number.isFinite(total)) {
                message += `: ${total.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })} total`;
              }
              if (truckCount > 0) {
                message += ` across ${truckCount} truck${truckCount === 1 ? '' : 's'}`;
              }
            }

            return message;
          }
          return "Shipping quote is complete for a lead";
        }

        // Deal-related admin notifications
        case AdminNotificationType.ADMIN_DEAL_CREATED_EVENT:
          const dealCreatedData = (notification as any).afterData;
          if (dealCreatedData) {
            let message = "New deal approval request created";
            
            // Add the name of the user who created the deal
            if (dealCreatedData.created_by_name) {
              message += ` by ${dealCreatedData.created_by_name}`;
            } else if (dealCreatedData.created_by && users.length > 0) {
              const createdByUser = users.find(user => user.id === dealCreatedData.created_by);
              if (createdByUser && createdByUser.first_name && createdByUser.last_name) {
                message += ` by ${createdByUser.first_name} ${createdByUser.last_name}`;
              }
            }
            
            // Add vehicle count
            if (dealCreatedData.vehicle_ids && dealCreatedData.vehicle_ids.length > 0) {
              message += ` for ${dealCreatedData.vehicle_ids.length} vehicle${dealCreatedData.vehicle_ids.length !== 1 ? 's' : ''}`;
            }
            
            // Add status
            if (dealCreatedData.status) {
              const status = dealCreatedData.status.charAt(0).toUpperCase() + dealCreatedData.status.slice(1);
              message += ` (Status: ${status})`;
            }
            
            return message;
          }
          return "New deal approval request has been created";

        case AdminNotificationType.ADMIN_DEAL_STATUS_CHANGE_EVENT: {
          type DealStatusVehicleDecisions = Record<string, { location?: { status: string }; condition?: { status: string }; final?: { status: string } }>;
          const dealAfter = (notification as any).afterData as { vehicle_ids?: string[]; vehicle_summaries?: { stock_number: string }[]; vehicle_decisions?: DealStatusVehicleDecisions } | undefined;
          const dealBefore = (notification as any).beforeData as { vehicle_decisions?: DealStatusVehicleDecisions } | undefined;
          if (!dealAfter) return "Deal approval request status has been updated";
          const afterDecisions = dealAfter.vehicle_decisions ?? {};
          const beforeDecisions = dealBefore?.vehicle_decisions ?? {};
          const getStockNumber = (vehicleId: string): string | null => {
            const idx = dealAfter.vehicle_ids?.indexOf(vehicleId) ?? -1;
            if (idx < 0 || !dealAfter.vehicle_summaries?.[idx]) return null;
            return dealAfter.vehicle_summaries[idx].stock_number;
          };
          type Stage = 'location' | 'condition' | 'final';
          const stages: Stage[] = ['location', 'condition', 'final'];
          const parts: string[] = [];
          for (const [vehicleId, decision] of Object.entries(afterDecisions)) {
            const prev = beforeDecisions[vehicleId];
            let stageOutcome: string | null = null;
            for (const stage of stages) {
              const afterStage = decision?.[stage];
              const prevStage = prev?.[stage];
              if (!afterStage?.status) continue;
              const isNew = !prevStage?.status;
              const statusChanged = prevStage && prevStage.status !== afterStage.status;
              if (isNew || statusChanged) {
                const stageLabel = stage.charAt(0).toUpperCase() + stage.slice(1);
                if (afterStage.status === 'approved') stageOutcome = `${stageLabel} approved`;
                else if (afterStage.status === 'conditionally_approved') stageOutcome = `${stageLabel} conditionally approved`;
                else if (afterStage.status === 'rejected') stageOutcome = `${stageLabel} rejected`;
                break;
              }
            }
            if (stageOutcome) {
              const stock = getStockNumber(vehicleId);
              parts.push(stock ? `Truck #${stock}: ${stageOutcome}` : stageOutcome);
            }
          }
          if (parts.length === 0) {
            let message = "Deal approval updated";
            if (dealAfter.vehicle_ids && dealAfter.vehicle_ids.length > 0) {
              message += ` (${dealAfter.vehicle_ids.length} vehicle${dealAfter.vehicle_ids.length !== 1 ? 's' : ''})`;
            }
            return message;
          }
          return `Deal approval updated: ${parts.join('; ')}`;
        }

        // Application-related admin notifications
        case AdminNotificationType.ADMIN_APPLICATION_CREATED_EVENT:
          const applicationCreatedData = (notification as any).afterData;
          if (applicationCreatedData && applicationCreatedData.user_name) {
            return `New application submitted by ${applicationCreatedData.user_name}`;
          }
          return "New application has been submitted";

        case AdminNotificationType.ADMIN_APPLICATION_STATUS_CHANGE_EVENT:
          const applicationStatusData = (notification as any).afterData;
          const beforeData = (notification as any).beforeData;
          if (applicationStatusData && applicationStatusData.user_name) {
            const statusChange = beforeData ? `${beforeData.status} → ${applicationStatusData.status}` : applicationStatusData.status;
            return `Application status changed for ${applicationStatusData.user_name}: ${statusChange}`;
          }
          return "Application status has been updated";

        // Inventory-related admin notifications
        case AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED: {
          const vehicleAddedData = (notification as any).afterData;
          if (vehicleAddedData && vehicleAddedData.stock_number) {
            let message = `New vehicle added to inventory: #${vehicleAddedData.stock_number}`;
            if (vehicleAddedData.year && vehicleAddedData.make && vehicleAddedData.model) {
              message += ` (${vehicleAddedData.year} ${vehicleAddedData.make} ${vehicleAddedData.model})`;
            }
            if (vehicleAddedData.condition) {
              message += ` - ${vehicleAddedData.condition}`;
            }
            if (vehicleAddedData.miles) {
              message += ` - ${vehicleAddedData.miles.toLocaleString()} miles`;
            }
            if (vehicleAddedData.entered_by && users.length > 0) {
              const enteredByUser = users.find(user => user.id === vehicleAddedData.entered_by);
              if (enteredByUser && enteredByUser.first_name && enteredByUser.last_name) {
                message += ` by ${enteredByUser.first_name} ${enteredByUser.last_name}`;
              }
            }
            return message;
          }
          return "New vehicle has been added to inventory";
        }

        case AdminNotificationType.ADMIN_INVENTORY_VEHICLE_DELETED: {
          const vehicleDeletedData = (notification as any).beforeData;
          if (vehicleDeletedData && vehicleDeletedData.stock_number) {
            let message = `Vehicle deleted from inventory: #${vehicleDeletedData.stock_number}`;
            if (vehicleDeletedData.year && vehicleDeletedData.make && vehicleDeletedData.model) {
              message += ` (${vehicleDeletedData.year} ${vehicleDeletedData.make} ${vehicleDeletedData.model})`;
            }
            if (vehicleDeletedData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === vehicleDeletedData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Vehicle has been deleted from inventory";
        }

        case AdminNotificationType.ADMIN_INVENTORY_PUBLISHED_ON_SITE_EVENT:
          const vehiclePublishedData = (notification as any).afterData;
          if (vehiclePublishedData && vehiclePublishedData.stock_number) {
            let message = `Vehicle published on site: #${vehiclePublishedData.stock_number}`;
            if (vehiclePublishedData.year && vehiclePublishedData.make && vehiclePublishedData.model) {
              message += ` (${vehiclePublishedData.year} ${vehiclePublishedData.make} ${vehiclePublishedData.model})`;
            }
            if (vehiclePublishedData.optic_list_price) {
              message += ` - $${vehiclePublishedData.optic_list_price.toLocaleString()}`;
            }
            // Add who published it
            if (vehiclePublishedData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === vehiclePublishedData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Vehicle has been published on the site";

        case AdminNotificationType.ADMIN_INVENTORY_SOLD_EVENT:
          const vehicleSoldData = (notification as any).afterData;
          if (vehicleSoldData && vehicleSoldData.stock_number) {
            let message = `Vehicle marked as sold: #${vehicleSoldData.stock_number}`;
            if (vehicleSoldData.year && vehicleSoldData.make && vehicleSoldData.model) {
              message += ` (${vehicleSoldData.year} ${vehicleSoldData.make} ${vehicleSoldData.model})`;
            }
            if (vehicleSoldData.sold_date) {
              const soldDate = new Date(vehicleSoldData.sold_date).toLocaleDateString();
              message += ` on ${soldDate}`;
            }
            // Add who sold it
            if (vehicleSoldData.sold_by_user && vehicleSoldData.sold_by_user.name) {
              message += ` by ${vehicleSoldData.sold_by_user.name}`;
            } else if (vehicleSoldData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === vehicleSoldData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            // Add sold to info if available
            if (vehicleSoldData.sold_to_user && vehicleSoldData.sold_to_user.name) {
              message += ` to ${vehicleSoldData.sold_to_user.name}`;
            }
            return message;
          }
          return "Vehicle has been marked as sold";

        case AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_NEEDED:
        case AdminNotificationType.STOCK_NUMBER_PICKUP_LOCATION_NEEDED:
          const pickupLocationData = (notification as any).afterData;
          if (pickupLocationData && pickupLocationData.stock_number) {
            let message = `Vehicle pickup location needed: #${pickupLocationData.stock_number}`;
            if (pickupLocationData.year && pickupLocationData.make && pickupLocationData.model) {
              message += ` (${pickupLocationData.year} ${pickupLocationData.make} ${pickupLocationData.model})`;
            }
            if (pickupLocationData.location) {
              message += ` - ${pickupLocationData.location}`;
            }
            // Add who requested it if available
            if (pickupLocationData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === pickupLocationData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` requested by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Vehicle pickup location is needed";

        case AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_NOT_PURCHASED:
          const pendingSaleNotPurchasedData = (notification as any).afterData;
          if (pendingSaleNotPurchasedData && pendingSaleNotPurchasedData.stock_number) {
            let message = `Pending sale not purchased: #${pendingSaleNotPurchasedData.stock_number}`;
            if (
              pendingSaleNotPurchasedData.year &&
              pendingSaleNotPurchasedData.make &&
              pendingSaleNotPurchasedData.model
            ) {
              message += ` (${pendingSaleNotPurchasedData.year} ${pendingSaleNotPurchasedData.make} ${pendingSaleNotPurchasedData.model})`;
            }
            if (pendingSaleNotPurchasedData.updated_by && users.length > 0) {
              const updatedByUser = users.find(
                (user) => user.id === pendingSaleNotPurchasedData.updated_by,
              );
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` requested by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Pending sale attempted on a truck not purchased for inventory";

        case AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_COMPLETED:
          const pickupLocationCompletedData = (notification as any).afterData;
          if (pickupLocationCompletedData && pickupLocationCompletedData.stock_number) {
            let message = `Vehicle pickup location completed: #${pickupLocationCompletedData.stock_number}`;
            if (pickupLocationCompletedData.year && pickupLocationCompletedData.make && pickupLocationCompletedData.model) {
              message += ` (${pickupLocationCompletedData.year} ${pickupLocationCompletedData.make} ${pickupLocationCompletedData.model})`;
            }
            if (pickupLocationCompletedData.pickup_location) {
              const loc = pickupLocationCompletedData.pickup_location;
              if (loc.city && loc.state) {
                message += ` - ${loc.city}, ${loc.state}`;
              }
            }
            // Add who completed it if available
            if (pickupLocationCompletedData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === pickupLocationCompletedData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Vehicle pickup location has been completed";

        case AdminNotificationType.ADMIN_VEHICLE_GATE_PASS_NEEDED:
        case AdminNotificationType.STOCK_NUMBER_GATE_PASS_NEEDED:
          const gatePassNeededData = (notification as any).afterData;
          if (gatePassNeededData && gatePassNeededData.totalVehicles != null) {
            const count = gatePassNeededData.totalVehicles;
            return `Gate pass needed: ${count} vehicle${count !== 1 ? "s" : ""}`;
          }
          return "Gate pass is needed for paid auction vehicles";

        case AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_TITLE_COPY_NEEDED:
        case AdminNotificationType.STOCK_NUMBER_PENDING_SALE_TITLE_COPY_NEEDED:
          const pendingSaleTitleCopyData = (notification as any).afterData;
          if (pendingSaleTitleCopyData && pendingSaleTitleCopyData.totalVehicles != null) {
            const count = pendingSaleTitleCopyData.totalVehicles;
            return `Pending sale title copies needed: ${count} vehicle${count !== 1 ? "s" : ""}`;
          }
          return "Pending sale vehicles need a title copy uploaded";

        case AdminNotificationType.ADMIN_VEHICLE_FUNDED_NEEDS_SOLD:
          const fundedNeedsSoldData = (notification as any).afterData;
          if (fundedNeedsSoldData && fundedNeedsSoldData.totalVehicles != null) {
            const count = fundedNeedsSoldData.totalVehicles;
            return `Funded vehicles need sold status: ${count} vehicle${count !== 1 ? "s" : ""}`;
          }
          return "Funded vehicles need to be marked Sold";

        case AdminNotificationType.ADMIN_VEHICLE_PURCHASED_NEEDS_PICKUP_LOCATION:
        case AdminNotificationType.STOCK_NUMBER_PURCHASED_NEEDS_PICKUP_LOCATION:
          const purchasedNeedsPickupData = (notification as any).afterData;
          if (purchasedNeedsPickupData && purchasedNeedsPickupData.totalVehicles != null) {
            const count = purchasedNeedsPickupData.totalVehicles;
            return `Purchased needs pickup location: ${count} vehicle${count !== 1 ? "s" : ""}`;
          }
          return "Purchased vehicles need a complete pickup location";

        case AdminNotificationType.ADMIN_VEHICLE_PAID_FOR_TITLE_REQUEST:
          const vehiclePaidForData = (notification as any).afterData;
          if (vehiclePaidForData && vehiclePaidForData.stock_number) {
            let message = `Vehicle marked paid for: #${vehiclePaidForData.stock_number}`;
            if (vehiclePaidForData.year && vehiclePaidForData.make && vehiclePaidForData.model) {
              message += ` (${vehiclePaidForData.year} ${vehiclePaidForData.make} ${vehiclePaidForData.model})`;
            }
            const transportSystemStatus = getTransportSystemStatusMessage(
              vehiclePaidForData.transport_system_status
            );
            if (transportSystemStatus) {
              message += ` - ${transportSystemStatus}`;
            }
            message += ". If you are the stock number owner, please request the title.";
            return message;
          }
          return "A vehicle has been marked as paid for. If you are the stock number owner, please request the title.";

        case AdminNotificationType.ADMIN_VEHICLE_FUNDED:
          const vehicleFundedData = (notification as any).afterData;
          if (vehicleFundedData && vehicleFundedData.stock_number) {
            let message = `Vehicle marked funded: #${vehicleFundedData.stock_number}`;
            if (vehicleFundedData.year && vehicleFundedData.make && vehicleFundedData.model) {
              message += ` (${vehicleFundedData.year} ${vehicleFundedData.make} ${vehicleFundedData.model})`;
            }
            return message;
          }
          return "A vehicle has been marked as funded";

        case AdminNotificationType.ADMIN_TITLE_REMINDER:
        case AdminNotificationType.STOCK_NUMBER_TITLE_REMINDER:
          const titleReminderData = (notification as any).afterData;
          if (titleReminderData?.vehicle?.stock_number != null && titleReminderData?.daysSincePayment != null) {
            let msg = `Title reminder: #${titleReminderData.vehicle.stock_number} - ${titleReminderData.daysSincePayment} days since payment`;
            if (titleReminderData.vehicle.year && titleReminderData.vehicle.make && titleReminderData.vehicle.model) {
              msg += ` (${titleReminderData.vehicle.year} ${titleReminderData.vehicle.make} ${titleReminderData.vehicle.model})`;
            }
            return msg;
          }
          return "Title reminder: Vehicle needs title";

        case AdminNotificationType.ADMIN_SOLD_TITLE_IN_TRANSIT_REMINDER:
          const soldTitleReminderData = (notification as any).afterData;
          if (
            soldTitleReminderData?.vehicle?.stock_number != null &&
            soldTitleReminderData?.daysSinceQualifying != null
          ) {
            const sellerType = soldTitleReminderData.vehicle.seller_type ?? "Unknown";
            let soldTitleMsg = `${sellerType} title reminder: #${soldTitleReminderData.vehicle.stock_number} - ${soldTitleReminderData.daysSinceQualifying} days since sold & paid`;
            if (
              soldTitleReminderData.vehicle.year &&
              soldTitleReminderData.vehicle.make &&
              soldTitleReminderData.vehicle.model
            ) {
              soldTitleMsg += ` (${soldTitleReminderData.vehicle.year} ${soldTitleReminderData.vehicle.make} ${soldTitleReminderData.vehicle.model})`;
            }
            return soldTitleMsg;
          }
          return "Sold title reminder: Vehicle needs title action";

        case AdminNotificationType.ADMIN_ELECTRONIC_TITLE_LIEN_RELEASE_REMINDER:
          const electronicTitleReminderData = (notification as any).afterData;
          if (
            electronicTitleReminderData?.vehicle?.stock_number != null &&
            electronicTitleReminderData?.daysSinceQualifying != null
          ) {
            const reminderDay = electronicTitleReminderData.reminderDay ?? electronicTitleReminderData.daysSinceQualifying;
            let electronicTitleMsg = `Electronic title reminder (day ${reminderDay}): #${electronicTitleReminderData.vehicle.stock_number} - has the bank sent the electronic lien release to the DMV?`;
            if (
              electronicTitleReminderData.vehicle.year &&
              electronicTitleReminderData.vehicle.make &&
              electronicTitleReminderData.vehicle.model
            ) {
              electronicTitleMsg += ` (${electronicTitleReminderData.vehicle.year} ${electronicTitleReminderData.vehicle.make} ${electronicTitleReminderData.vehicle.model})`;
            }
            return electronicTitleMsg;
          }
          return "Electronic title reminder: Has the bank sent the electronic lien release to the DMV?";

        case AdminNotificationType.ADMIN_TITLE_OVERVIEW:
          const adminTitleOverviewData = (notification as any).afterData;
          if (adminTitleOverviewData && adminTitleOverviewData.actionRequired != null) {
            const count = adminTitleOverviewData.actionRequired;
            return `Title overview: ${count} vehicle${count !== 1 ? "s" : ""} need${count === 1 ? "s" : ""} title action`;
          }
          return "Title overview: Vehicles need title action";

        case AdminNotificationType.ADMIN_TITLE_RECEIVED:
          const titleReceivedData = (notification as any).afterData;
          if (titleReceivedData && titleReceivedData.stock_number) {
            let message = `Title received: #${titleReceivedData.stock_number}`;
            if (titleReceivedData.year && titleReceivedData.make && titleReceivedData.model) {
              message += ` (${titleReceivedData.year} ${titleReceivedData.make} ${titleReceivedData.model})`;
            }
            if (titleReceivedData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === titleReceivedData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Physical title has been received for a vehicle";

        case AdminNotificationType.ADMIN_INVENTORY_PAYOFF_EXPIRING:
          const inventoryPayoffData = (notification as any).afterData;
          if (inventoryPayoffData && inventoryPayoffData.stock_number) {
            let message = `Payoff expiring: #${inventoryPayoffData.stock_number}`;
            if (inventoryPayoffData.year && inventoryPayoffData.make && inventoryPayoffData.model) {
              message += ` (${inventoryPayoffData.year} ${inventoryPayoffData.make} ${inventoryPayoffData.model})`;
            }
            if (inventoryPayoffData.lien_good_through_date) {
              message += ` - good through ${new Date(inventoryPayoffData.lien_good_through_date).toLocaleDateString()}`;
            }
            return message;
          }
          return "Vehicle payoff is expiring soon";

        case AdminNotificationType.SALES_REP_REPAIR_COMPLETE:
          const repairCompleteData = (notification as any).afterData;
          if (repairCompleteData && repairCompleteData.stock_number) {
            let message = `Repair complete: #${repairCompleteData.stock_number}`;
            if (repairCompleteData.year && repairCompleteData.make && repairCompleteData.model) {
              message += ` (${repairCompleteData.year} ${repairCompleteData.make} ${repairCompleteData.model})`;
            }
            message += " is ready for pickup from the repair shop";
            return message;
          }
          return "A truck you sold has completed repair and is ready for pickup";

        case AdminNotificationType.SALES_REP_DELIVERY_COMPLETE:
          const deliveryCompleteData = (notification as any).afterData;
          if (deliveryCompleteData && deliveryCompleteData.stock_number) {
            let message = `Delivery complete: #${deliveryCompleteData.stock_number}`;
            if (deliveryCompleteData.year && deliveryCompleteData.make && deliveryCompleteData.model) {
              message += ` (${deliveryCompleteData.year} ${deliveryCompleteData.make} ${deliveryCompleteData.model})`;
            }
            message += " has been delivered";
            return message;
          }
          return "A truck you sold has been delivered";

        case AdminNotificationType.SALES_REP_VEHICLE_FUNDED:
          const salesRepFundedData = (notification as any).afterData;
          if (salesRepFundedData && salesRepFundedData.stock_number) {
            let message = `Transaction funded: #${salesRepFundedData.stock_number}`;
            if (salesRepFundedData.year && salesRepFundedData.make && salesRepFundedData.model) {
              message += ` (${salesRepFundedData.year} ${salesRepFundedData.make} ${salesRepFundedData.model})`;
            }
            message += " — send the customer Post Sale Email";
            return message;
          }
          return "A truck you sold was marked funded — send the customer Post Sale Email";

        case AdminNotificationType.ADMIN_MANAGEMENT_REPAIR_COMPLETE:
          const mgmtRepairData = (notification as any).afterData;
          if (mgmtRepairData && mgmtRepairData.stock_number) {
            let message = `Repair complete: #${mgmtRepairData.stock_number}`;
            if (mgmtRepairData.year && mgmtRepairData.make && mgmtRepairData.model) {
              message += ` (${mgmtRepairData.year} ${mgmtRepairData.make} ${mgmtRepairData.model})`;
            }
            message += " is ready for pickup from the repair shop";
            return message;
          }
          return "A truck has completed repair and is ready for pickup";

        case AdminNotificationType.ADMIN_MANAGEMENT_DELIVERY_COMPLETE:
          const mgmtDeliveryData = (notification as any).afterData;
          if (mgmtDeliveryData && mgmtDeliveryData.stock_number) {
            let message = `Delivery complete: #${mgmtDeliveryData.stock_number}`;
            if (mgmtDeliveryData.year && mgmtDeliveryData.make && mgmtDeliveryData.model) {
              message += ` (${mgmtDeliveryData.year} ${mgmtDeliveryData.make} ${mgmtDeliveryData.model})`;
            }
            message += " has been delivered";
            return message;
          }
          return "A truck has been delivered";

      case AdminNotificationType.ADMIN_OVERDUE_VEHICLE_REMINDER:
        const overdueData = (notification as any).afterData;
        if (overdueData && overdueData.totalOverdueVehicles != null) {
          const count = overdueData.totalOverdueVehicles;
          return `${count} vehicle${count !== 1 ? "s" : ""} ${count === 1 ? "is" : "are"} overdue for pickup`;
        }
        return "Vehicles are overdue for pickup";

        case AdminNotificationType.ADMIN_REPAIR_5_DAYS_REMINDER:
          const repair5Data = (notification as any).afterData;
          if (repair5Data && repair5Data.totalVehicles != null) {
            const count = repair5Data.totalVehicles;
            return `${count} vehicle${count !== 1 ? "s" : ""} ${count === 1 ? "has" : "have"} been in repair 5+ days`;
          }
          return "Vehicles have been in repair 5+ days";

        case AdminNotificationType.STOCK_NUMBER_TITLE_OVERVIEW:
          const titleOverviewData = (notification as any).afterData;
          if (titleOverviewData && titleOverviewData.actionRequired != null) {
            const count = titleOverviewData.actionRequired;
            return `Title overview: ${count} vehicle${count !== 1 ? "s" : ""} need${count === 1 ? "s" : ""} title action`;
          }
          return "Title overview: Vehicles need title action";

        case AdminNotificationType.STOCK_NUMBER_PAYOFF_EXPIRING:
          const payoffData = (notification as any).afterData;
          if (payoffData && payoffData.stock_number) {
            let message = `Payoff expiring: #${payoffData.stock_number}`;
            if (payoffData.year && payoffData.make && payoffData.model) {
              message += ` (${payoffData.year} ${payoffData.make} ${payoffData.model})`;
            }
            if (payoffData.lien_good_through_date) {
              message += ` - good through ${new Date(payoffData.lien_good_through_date).toLocaleDateString()}`;
            }
            return message;
          }
          return "Vehicle payoff is expiring soon";

        case AdminNotificationType.ADMIN_TRANSPORTER_VEHICLE_READY_FOR_TRANSPORT:
          const readyForTransportData = (notification as any).afterData;
          if (readyForTransportData && readyForTransportData.stock_number) {
            let message = `Vehicle ready for transport: #${readyForTransportData.stock_number}`;
            if (readyForTransportData.year && readyForTransportData.make && readyForTransportData.model) {
              message += ` (${readyForTransportData.year} ${readyForTransportData.make} ${readyForTransportData.model})`;
            }
            
            // Add transport types
            const transportTypes: string[] = [];
            if (readyForTransportData.ready_for_transport?.customer) {
              transportTypes.push("Customer");
            }
            if (readyForTransportData.ready_for_transport?.repair) {
              transportTypes.push("Repair");
            }
            if (transportTypes.length > 0) {
              message += ` - ${transportTypes.join(" and ")}`;
            }

            const transportSystemStatus = getTransportSystemStatusMessage(
              readyForTransportData.transport_system_status
            );
            if (transportSystemStatus) {
              message += ` - ${transportSystemStatus}`;
            }
            
            // Add who marked it ready if available
            if (readyForTransportData.updated_by && users.length > 0) {
              const updatedByUser = users.find(user => user.id === readyForTransportData.updated_by);
              if (updatedByUser && updatedByUser.first_name && updatedByUser.last_name) {
                message += ` by ${updatedByUser.first_name} ${updatedByUser.last_name}`;
              }
            }
            return message;
          }
          return "Vehicle has been marked as ready for transport";

        case AdminNotificationType.ADMIN_TRANSPORTER_PHOTOS_COMPLETED:
          const photosCompletedData = (notification as any).afterData;
          if (photosCompletedData && photosCompletedData.vehicleId) {
            // Find the vehicle in the vehicles array
            const vehicle = vehicles.find(v => v.id === photosCompletedData.vehicleId);
            let message = "Transporter photos completed";
            
            if (vehicle) {
              message += ` for ${vehicle.year} ${vehicle.make} ${vehicle.model}`;
              if (vehicle.stock_number) {
                message += ` (#${vehicle.stock_number})`;
              }
            }
            
            // Add completion details if available
            if (photosCompletedData.preDeliveryComplete && photosCompletedData.postDeliveryComplete) {
              message += " - Both pre and post-delivery photos completed";
            } else if (photosCompletedData.preDeliveryComplete) {
              message += " - Pre-delivery photos completed";
            } else if (photosCompletedData.postDeliveryComplete) {
              message += " - Post-delivery photos completed";
            }

            if (photosCompletedData.requiresAdminApproval === true) {
              message += " — ❗ admin approval required";
            } else if (photosCompletedData.requiresAdminApproval === false) {
              message += " — no admin approval needed";
            }
            
            // Add transporter info if available
            if (photosCompletedData.transporterName) {
              message += ` by ${photosCompletedData.transporterName}`;
            }
            
            return message;
          }
          return "Transporter photos have been completed";

        case AdminNotificationType.ADMIN_TRANSPORTER_PACKAGE_COMPLETION_REQUESTED:
          const packageCompletionData = (notification as any).afterData;
          if (
            packageCompletionData &&
            typeof packageCompletionData.notification_leg_index === "number" &&
            Array.isArray(packageCompletionData.shipment_legs)
          ) {
            const from = packageCompletionData.notification_from_label || "Origin";
            const to = packageCompletionData.notification_to_label || "Destination";
            let message = `Shipment completion requested: ${from} → ${to}`;
            if (packageCompletionData.notification_vehicle_label) {
              message += ` (${packageCompletionData.notification_vehicle_label})`;
            }
            message += ` — leg ${packageCompletionData.notification_leg_index + 1}`;
            if ((notification as any).actorId && users.length > 0) {
              const requestingUser = users.find((user) => user.id === (notification as any).actorId);
              if (requestingUser?.first_name && requestingUser?.last_name) {
                message += ` by ${requestingUser.first_name} ${requestingUser.last_name}`;
              }
            }
            return message;
          }
          if (packageCompletionData && packageCompletionData.name) {
            let message = `Package completion requested: ${packageCompletionData.name}`;
            
            // Add vehicle count if available
            if (packageCompletionData.vehicle_ids && packageCompletionData.vehicle_ids.length > 0) {
              message += ` (${packageCompletionData.vehicle_ids.length} vehicle${packageCompletionData.vehicle_ids.length !== 1 ? 's' : ''})`;
            }
            
            // Add status if available
            if (packageCompletionData.status) {
              message += ` - Status: ${packageCompletionData.status}`;
            }
            
            // Add who requested it if available
            if (packageCompletionData.actorId && users.length > 0) {
              const requestingUser = users.find(user => user.id === packageCompletionData.actorId);
              if (requestingUser && requestingUser.first_name && requestingUser.last_name) {
                message += ` by ${requestingUser.first_name} ${requestingUser.last_name}`;
              }
            } else if ((notification as any).actorId && users.length > 0) {
              const requestingUser = users.find(user => user.id === (notification as any).actorId);
              if (requestingUser && requestingUser.first_name && requestingUser.last_name) {
                message += ` by ${requestingUser.first_name} ${requestingUser.last_name}`;
              }
            }
            
            return message;
          }
          return "Package completion has been requested";

        case AdminNotificationType.ADMIN_TRANSPORTER_DRIVER_CREATION_REQUEST:
          const driverRequestData = (notification as any).afterData;
          if (driverRequestData && driverRequestData.email) {
            let message = "Driver creation request submitted";
            if (driverRequestData.first_name || driverRequestData.last_name) {
              const fullName =
                `${driverRequestData.first_name || ""} ${driverRequestData.last_name || ""}`.trim();
              if (fullName) {
                message += `: ${fullName}`;
              }
            }
            message += ` (${driverRequestData.email})`;
            return message;
          }
          return "Driver creation request submitted";

        case AdminNotificationType.ADMIN_TRANSPORTER_INSPECTION_CHECKLIST_COMPLETED:
          const checklistCompletedData = (notification as any).afterData;
          if (checklistCompletedData) {
            const vehicleLabel =
              typeof checklistCompletedData.notification_vehicle_label === "string"
                ? checklistCompletedData.notification_vehicle_label.trim()
                : "";
            const vinLast4 = formatLast4Vin(checklistCompletedData.notification_vehicle_vin);
            if (vehicleLabel && vinLast4) {
              return `Inspection checklist completed for ${vehicleLabel} (${vinLast4})`;
            }
            if (vehicleLabel) {
              return `Inspection checklist completed for ${vehicleLabel}`;
            }
            if (checklistCompletedData.transport_truck_id) {
              return `Inspection checklist completed for transport truck ${checklistCompletedData.transport_truck_id}`;
            }
          }
          return "Inspection checklist completed";

        case AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_ESTIMATE_UPLOADED: {
          const estimateData = (notification as any).afterData;
          if (estimateData) {
            const vehicleLabel =
              typeof estimateData.notification_vehicle_label === "string"
                ? estimateData.notification_vehicle_label.trim()
                : "";
            const vinLast4 = formatLast4Vin(estimateData.notification_vehicle_vin);
            if (vehicleLabel && vinLast4) {
              return `Repair estimate uploaded for ${vehicleLabel} (${vinLast4})`;
            }
            if (vehicleLabel) {
              return `Repair estimate uploaded for ${vehicleLabel}`;
            }
            if (estimateData.transport_truck_id) {
              return `Repair estimate uploaded for transport truck ${estimateData.transport_truck_id}`;
            }
          }
          return "Repair estimate uploaded";
        }

        case AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_TIMELINE_UPDATED: {
          const timelineData = (notification as any).afterData;
          const timelineUpdates = Array.isArray(timelineData?.notification_timeline_updates)
            ? timelineData.notification_timeline_updates
            : [];
          if (timelineUpdates.length > 0) {
            const count = timelineUpdates.length;
            const peopleCount = new Set(
              timelineUpdates
                .map((update: any) =>
                  typeof update?.actorName === "string" ? update.actorName.trim() : ""
                )
                .filter(Boolean)
            ).size;
            const vehicleCount = new Set(
              timelineUpdates
                .map((update: any) =>
                  typeof update?.vehicleLabel === "string" ? update.vehicleLabel.trim() : ""
                )
                .filter(Boolean)
            ).size;
            return `${count} repair timeline update${count === 1 ? "" : "s"} from ${peopleCount} user${peopleCount === 1 ? "" : "s"} across ${vehicleCount} truck${vehicleCount === 1 ? "" : "s"}`;
          }

          const action = timelineData?.notification_timeline_action;
          const actorName = timelineData?.notification_timeline_user_name || "Repair user";
          const vehicleLabel =
            typeof timelineData?.notification_vehicle_label === "string"
              ? timelineData.notification_vehicle_label.trim()
              : "";
          const noteText =
            typeof timelineData?.notification_timeline_note === "string"
              ? timelineData.notification_timeline_note.trim()
              : "";
          const actionLabel =
            action === "work_status"
              ? "updated work status"
              : action === "photo"
                ? "uploaded a photo"
                : action === "video"
                  ? "uploaded a video"
                  : action === "invoice"
                    ? "uploaded an invoice"
                  : "added a note";

          let message = `${actorName} ${actionLabel}`;
          if (vehicleLabel) {
            message += ` for ${vehicleLabel}`;
          } else if (timelineData?.transport_truck_id) {
            message += ` for transport truck ${timelineData.transport_truck_id}`;
          }
          if (noteText) {
            message += `: ${noteText}`;
          }
          return message;
        }

        case AdminNotificationType.ADMIN_TRANSPORTER_SHIPMENT_REJECTED: {
          const shipmentRejectedData = (notification as any).afterData;
          if (shipmentRejectedData?.id) {
            return `Shipment rejected by assigned transporter company (plan ${shipmentRejectedData.id})`;
          }
          return "Shipment rejected by assigned transporter company";
        }

        case AdminNotificationType.ADMIN_PART_REQUESTED: {
          const partRequestData = (notification as any).afterData;
          if (partRequestData && partRequestData.name) {
            let message = `Part requested: ${partRequestData.name}`;
            if (partRequestData.sku) {
              message += ` (${partRequestData.sku})`;
            }
            if (partRequestData.quantity) {
              message += ` ×${partRequestData.quantity}`;
            }
            if (partRequestData.repair_shop_name) {
              message += ` from ${partRequestData.repair_shop_name}`;
            }
            if (partRequestData.requested_by_name) {
              message += ` by ${partRequestData.requested_by_name}`;
            }
            return message;
          }
          return "A part has been requested";
        }

        case AdminNotificationType.ADMIN_PTO_REQUEST_CREATED: {
          const ptoRequestData = (notification as any).afterData;
          const requester = users.find((user) => user.id === ptoRequestData?.userId);
          const employeeName = requester
            ? `${requester.first_name || ""} ${requester.last_name || ""}`.trim() ||
              requester.email ||
              "An employee"
            : "An employee";

          if (ptoRequestData?.daysRequested) {
            return `PTO request: ${employeeName} requested ${ptoRequestData.daysRequested} day(s) off (${ptoRequestData.startDate} - ${ptoRequestData.endDate})`;
          }
          return `PTO request submitted by ${employeeName}`;
        }

        case AdminNotificationType.ADMIN_TRANSPORTER_SLA_OVERDUE_DAILY: {
          const slaData = (notification as any).afterData;
          const overdueVehicles = Array.isArray(slaData?.overdueVehicles) ? slaData.overdueVehicles : [];
          const totalOverdueItems =
            typeof slaData?.totalOverdueItems === "number"
              ? slaData.totalOverdueItems
              : overdueVehicles.reduce(
                  (sum: number, vehicle: any) =>
                    sum + (Array.isArray(vehicle?.overdueSteps) ? vehicle.overdueSteps.length : 0),
                  0
                );
          const totalVehicles =
            typeof slaData?.totalVehicles === "number" ? slaData.totalVehicles : overdueVehicles.length;

          if (overdueVehicles.length > 0) {
            const details = overdueVehicles
              .map((vehicle: any) => {
                const vehicleLabel =
                  vehicle?.stockNumber != null
                    ? `#${vehicle.stockNumber}`
                    : vehicle?.vin
                      ? `VIN ${vehicle.vin}`
                      : "Unknown vehicle";
                const stepLabels = (Array.isArray(vehicle?.overdueSteps) ? vehicle.overdueSteps : [])
                  .map((step: any) => step?.label)
                  .filter(Boolean)
                  .join(", ");
                return stepLabels ? `${vehicleLabel}: ${stepLabels}` : vehicleLabel;
              })
              .filter(Boolean)
              .join(" | ");

            return `Transport SLA overdue (${totalOverdueItems} item${totalOverdueItems === 1 ? "" : "s"} across ${totalVehicles} vehicle${totalVehicles === 1 ? "" : "s"}): ${details}`;
          }

          if (typeof totalOverdueItems === "number") {
            return `Transport SLA overdue: ${totalOverdueItems} item${totalOverdueItems === 1 ? "" : "s"} across ${totalVehicles} vehicle${totalVehicles === 1 ? "" : "s"}`;
          }
          return "Transport SLA has overdue timeline items";
        }
      }
    } catch (error) {
      console.error('Error formatting notification message:', error);
      // Fall through to default messages below
    }
  }

  // If we get here, either it's not an admin notification type or there was an error
  // Provide more descriptive fallback messages based on the type string
  if (type) {
    const typeLower = type.toLowerCase();
    
    if (typeLower.includes('user')) {
      return "User-related notification";
    } else if (typeLower.includes('inventory') || typeLower.includes('vehicle')) {
      return "Vehicle inventory notification";
    } else if (typeLower.includes('application')) {
      return "Application notification";
    } else     if (typeLower.includes('lead')) {
      return "Lead notification";
    } else if (typeLower.includes('deal')) {
      return "Deal notification";
    } else if (typeLower.includes('admin')) {
      return "Administrative notification";
    }
  }

  // Final fallback
  return "New notification";
};

/**
 * Gets a detailed description for a notification
 * @param notification The notification object
 * @returns Detailed description or undefined if not available
 */
export const getNotificationDescription = (
  notification: INotification
): string | undefined => {
  const data = notification as any;

  // If the notification has a description, use it
  if (data.data?.description) {
    return data.data.description;
  }

  // Otherwise return undefined
  return undefined;
};

/**
 * Type guard to check if a value is a valid severity
 */
function isValidSeverity(
  value: any
): value is "info" | "success" | "warning" | "error" {
  return ["info", "success", "warning", "error"].includes(value);
}

/**
 * Gets the severity level for a notification
 * @param notification The notification object
 * @returns Severity level ('info', 'success', 'warning', 'error')
 */
export const getNotificationSeverity = (
  notification: INotification
): "info" | "success" | "warning" | "error" => {
  const { type } = notification;
  const data = notification as any;

  // If the notification has a severity, use it
  if ("severity" in data && data.severity && isValidSeverity(data.severity)) {
    return data.severity;
  }

  // Handle admin notification types
  if (isAdminNotificationType(type)) {
    switch (type) {
      // User-related admin notifications
      case AdminNotificationType.ADMIN_USER_SET_INITIAL_PASSWORD:
      case AdminNotificationType.ADMIN_USER_CREATED_EVENT:
        return "success";

      // Lead-related admin notifications
      case AdminNotificationType.ADMIN_LEAD_CREATED_EVENT:
        return "info";

      case AdminNotificationType.ADMIN_LEAD_SHIPPING_INFORMATION_NEEDED:
        return "warning";

      case AdminNotificationType.ADMIN_LEAD_SHIPPING_QUOTE_COMPLETE:
        return "success";

      // Deal-related admin notifications
      case AdminNotificationType.ADMIN_DEAL_CREATED_EVENT:
        return "warning";
      
      case AdminNotificationType.ADMIN_DEAL_STATUS_CHANGE_EVENT: {
        type DealStatusVehicleDecisionsSeverity = Record<string, { location?: { status: string }; condition?: { status: string }; final?: { status: string } }>;
        const dealAfterSeverity = (notification as any).afterData as { vehicle_decisions?: DealStatusVehicleDecisionsSeverity } | undefined;
        const dealBeforeSeverity = (notification as any).beforeData as { vehicle_decisions?: DealStatusVehicleDecisionsSeverity } | undefined;
        const afterD = dealAfterSeverity?.vehicle_decisions ?? {};
        const beforeD = dealBeforeSeverity?.vehicle_decisions ?? {};
        const stageKeys = ['location', 'condition', 'final'] as const;
        let hasRejected = false;
        let hasApproved = false;
        for (const [vehicleId, decision] of Object.entries(afterD)) {
          const prev = beforeD[vehicleId];
          for (const stage of stageKeys) {
            const afterStage = (decision as any)?.[stage];
            const prevStage = prev?.[stage];
            if (!afterStage?.status) continue;
            const isNew = !prevStage?.status;
            const statusChanged = prevStage && prevStage.status !== afterStage.status;
            if (isNew || statusChanged) {
              if (afterStage.status === 'rejected') hasRejected = true;
              if (afterStage.status === 'approved' || afterStage.status === 'conditionally_approved') hasApproved = true;
            }
          }
        }
        if (hasRejected) return "error";
        if (hasApproved) return "success";
        return "warning";
      }

      // Application-related admin notifications
      case AdminNotificationType.ADMIN_APPLICATION_CREATED_EVENT:
        return "info";
      
      case AdminNotificationType.ADMIN_APPLICATION_STATUS_CHANGE_EVENT:
        return "warning";

      // Inventory-related admin notifications
      case AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED:
        return "success";
      case AdminNotificationType.ADMIN_INVENTORY_VEHICLE_DELETED:
        return "error";
      
      case AdminNotificationType.ADMIN_INVENTORY_PUBLISHED_ON_SITE_EVENT:
        return "success";
      
      case AdminNotificationType.ADMIN_INVENTORY_SOLD_EVENT:
        return "success";
      
      case AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_NEEDED:
      case AdminNotificationType.STOCK_NUMBER_PICKUP_LOCATION_NEEDED:
      case AdminNotificationType.ADMIN_VEHICLE_GATE_PASS_NEEDED:
      case AdminNotificationType.STOCK_NUMBER_GATE_PASS_NEEDED:
      case AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_TITLE_COPY_NEEDED:
      case AdminNotificationType.STOCK_NUMBER_PENDING_SALE_TITLE_COPY_NEEDED:
      case AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_NOT_PURCHASED:
      case AdminNotificationType.ADMIN_VEHICLE_FUNDED_NEEDS_SOLD:
      case AdminNotificationType.ADMIN_VEHICLE_PURCHASED_NEEDS_PICKUP_LOCATION:
      case AdminNotificationType.STOCK_NUMBER_PURCHASED_NEEDS_PICKUP_LOCATION:
        return "warning";
      
      case AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_COMPLETED:
        return "success";
      
      case AdminNotificationType.ADMIN_VEHICLE_PAID_FOR_TITLE_REQUEST:
        return "info";

      case AdminNotificationType.ADMIN_VEHICLE_FUNDED:
      case AdminNotificationType.ADMIN_TITLE_RECEIVED:
      case AdminNotificationType.ADMIN_TITLE_OVERVIEW:
      case AdminNotificationType.SALES_REP_REPAIR_COMPLETE:
      case AdminNotificationType.SALES_REP_DELIVERY_COMPLETE:
      case AdminNotificationType.SALES_REP_VEHICLE_FUNDED:
      case AdminNotificationType.ADMIN_MANAGEMENT_REPAIR_COMPLETE:
      case AdminNotificationType.ADMIN_MANAGEMENT_DELIVERY_COMPLETE:
        return "success";

      // Transporter-related admin notifications
      case AdminNotificationType.ADMIN_OVERDUE_VEHICLE_REMINDER:
      case AdminNotificationType.ADMIN_REPAIR_5_DAYS_REMINDER:
      case AdminNotificationType.ADMIN_TITLE_OVERVIEW:
      case AdminNotificationType.ADMIN_TITLE_REMINDER:
      case AdminNotificationType.ADMIN_SOLD_TITLE_IN_TRANSIT_REMINDER:
      case AdminNotificationType.ADMIN_ELECTRONIC_TITLE_LIEN_RELEASE_REMINDER:
      case AdminNotificationType.ADMIN_TRANSPORTER_SLA_OVERDUE_DAILY:
        return "warning";
      case AdminNotificationType.ADMIN_INVENTORY_PAYOFF_EXPIRING:
      case AdminNotificationType.STOCK_NUMBER_PAYOFF_EXPIRING:
      case AdminNotificationType.STOCK_NUMBER_TITLE_OVERVIEW:
      case AdminNotificationType.STOCK_NUMBER_TITLE_REMINDER:
        return "warning";
      case AdminNotificationType.ADMIN_TRANSPORTER_PHOTOS_COMPLETED:
        return "success";
      
      case AdminNotificationType.ADMIN_TRANSPORTER_VEHICLE_READY_FOR_TRANSPORT:
        return "success";
      
      case AdminNotificationType.ADMIN_TRANSPORTER_PACKAGE_COMPLETION_REQUESTED:
      case AdminNotificationType.ADMIN_TRANSPORTER_DRIVER_CREATION_REQUEST:
      case AdminNotificationType.ADMIN_TRANSPORTER_INSPECTION_CHECKLIST_COMPLETED:
      case AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_ESTIMATE_UPLOADED:
      case AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_TIMELINE_UPDATED:
      case AdminNotificationType.ADMIN_TRANSPORTER_SHIPMENT_REJECTED:
      case AdminNotificationType.ADMIN_PART_REQUESTED:
      case AdminNotificationType.ADMIN_PTO_REQUEST_CREATED:
        return "info";
    }
  }

  // Default severity
  return "info";
};
